import { supplierApi } from '@/main/api';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KOGAutoCompletePaginationControlled from '@/main/components/shared/inputAutoCompleteWithPagination/autoCompletePaginationControlled';
import KogInputAmountControlled from '@/main/components/shared/inputText/inputAmountControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import WrapperQuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { TextEllipsis } from '@/main/components/shared/styles';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import { SearchTermRequest } from '@/main/models/common/searchTermRequest';
import { SupplierFavoriteRequest } from '@/main/models/request';
import { QuoteDirectAssignItemsResponse } from '@/main/models/response';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import {
  getTranslateByKey,
  isEmpty,
  isNullUndefinedEmpty
} from '@/main/services/utils';
import { usePartTypeReducer } from '@/main/store/partType';
import colors from '@/main/style/colors';
import { Warning } from '@mui/icons-material';
import { TableCell } from '@mui/material';
import React, { memo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { BuyerPartForms, renderPrices } from '../modalManualAnswerUtils';
import SkeletonModalRequoteParts from '../skeletonModalBuyerManualAnswer';
import { GRID_COL, GridColIndex } from '../useGridTabNavigation';
import * as S from './styles';

interface IPartBuyerManualAnswerProps {
  part: QuoteDirectAssignItemsResponse;
  isLoadingParts: boolean;
  index: number;
  guidCompanyBuyer: string;
  onGridTab: (e: React.KeyboardEvent, row: number, col: GridColIndex) => void;
}

function PartBuyerManualAnswer({
  part,
  isLoadingParts,
  index,
  guidCompanyBuyer,
  onGridTab
}: IPartBuyerManualAnswerProps) {
  const { partType: partTypeReducer } = usePartTypeReducer();
  const methods = useFormContext<BuyerPartForms>();
  const { control } = methods;

  const partValues = useWatch({ control, name: `parts.${index}` });

  const requiredFields =
    !isNullUndefinedEmpty(partValues.supplier) ||
    !isNullUndefinedEmpty(partValues.partType) ||
    !isNullUndefinedEmpty(partValues.deadline) ||
    !isNullUndefinedEmpty(partValues.price);

  async function getSuppliersFavoriteAPI(requestPagination: SearchTermRequest) {
    const request: SupplierFavoriteRequest = {
      ...requestPagination,
      guidCompanyBuyer: guidCompanyBuyer
    };
    const response =
      await supplierApi.getFavoriteSuppliersByCompanyBuyer(request);
    return response;
  }

  if (isLoadingParts) return <>{<SkeletonModalRequoteParts key={index} />}</>;
  if (isNullUndefinedEmpty(part)) return <ResultNotFoundComponent />;

  const { notes } = part.quoteProcurementPartInfo;
  const isTireGlass =
    part.quoteProcurementPartInfo.idPartType == PartTypeEnum.Tire ||
    part.quoteProcurementPartInfo.idPartType == PartTypeEnum.Glass;
  const showIcon = notes || isTireGlass;

  return (
    <S.StyledTableRow>
      <TableCell align="left">
        <TooltipComponent
          interactive
          placement="top-start"
          PopperProps={{
            disablePortal: true
          }}
          title={
            <WrapperQuoteProcurementPartInfo
              data={part.quoteProcurementPartInfo}
              renderType="column"
              isTooltip={false}
            />
          }>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row'
            }}>
            <div
              style={{
                minWidth: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
              <TextEllipsis>
                <strong>
                  {part.quoteProcurementPartInfo.quoteItemDescription}
                </strong>
                {!isNullUndefinedEmpty(
                  part.quoteProcurementPartInfo.quoteItemPartNumber
                ) && (
                  <>
                    {' - '}
                    <span>
                      {part.quoteProcurementPartInfo.quoteItemPartNumber}
                    </span>
                  </>
                )}
              </TextEllipsis>
              <TextEllipsis>
                {!isEmpty(part.quoteProcurementPartInfo.tireInfo) && (
                  <span>
                    {part.quoteProcurementPartInfo.tireInfo.slice(1, -1)}
                  </span>
                )}

                {!isEmpty(part.quoteProcurementPartInfo.glassInfo) && (
                  <span>
                    {part.quoteProcurementPartInfo.glassInfo.slice(1, -1)}
                  </span>
                )}
              </TextEllipsis>
            </div>
            {showIcon && (
              <span className="icon">
                <Warning style={{ color: colors.orange }} fontSize="small" />
              </span>
            )}
          </div>
        </TooltipComponent>
      </TableCell>
      <TableCell align="center">{renderPrices(part)}</TableCell>
      <S.InputTableCell>
        <KOGAutoCompletePaginationControlled
          required={requiredFields}
          idScreen={`${index}-id-buyerManualAnswer`}
          name={`parts.${index}.supplier`}
          label={getTranslateByKey('information.supplier')}
          loadingMessage={`${getTranslateByKey(
            'information.searchingForSuppliers'
          )}...`}
          control={control}
          getData={getSuppliersFavoriteAPI}
          className="customBorder"
          typeInput="outlined"
          onKeyDown={e => onGridTab(e, index, GRID_COL.SUPPLIER)}
        />
      </S.InputTableCell>
      <S.InputTableCell>
        <KogAutoCompleteControlled
          id={`${index}-id-buyerManualAnswer-select-partType`}
          nameCustom={`parts.${index}.partType`}
          label={getTranslateByKey('information.partTypes')}
          control={control}
          options={partTypeReducer.data}
          isLoading={partTypeReducer.isLoading}
          onChangeCustom={() => {}}
          rulesCustom={{ required: requiredFields }}
          className="customBorder"
          variant="outlined"
          onKeyDown={e => onGridTab(e, index, GRID_COL.PART_TYPE)}
        />
      </S.InputTableCell>
      <S.InputTableCell>
        <KogInputTextControlledNew
          id={`${index}-id-buyerManualAnswer-txt-deadline`}
          name={`parts.${index}.deadline`}
          variant="outlined"
          label={getTranslateByKey('information.deadline')}
          hasOnlyNumberInteger
          control={control}
          inputProps={{ maxLength: 3, className: 'customInputHeight' }}
          controllerProps={{ rules: { required: requiredFields } }}
          onKeyDown={e => onGridTab(e, index, GRID_COL.DEADLINE)}
        />
      </S.InputTableCell>
      <S.InputTableCell>
        <KogInputAmountControlled
          id={`${index}-id-buyerManualAnswer-txt-price`}
          name={`parts.${index}.price`}
          typeInput="outlinedfull"
          label={getTranslateByKey('information.price')}
          control={control}
          required={requiredFields}
          controllerProps={{
            rules: { maxLength: 10, required: requiredFields }
          }}
          onKeyDown={e => onGridTab(e, index, GRID_COL.PRICE)}
        />
      </S.InputTableCell>
    </S.StyledTableRow>
  );
}

export default memo(PartBuyerManualAnswer);
